import { Button, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { Util } from '@finley-technologies/finley-types';

import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  useCreateAssignmentServicerReportPreviewMutation,
  useGetLatestCompletedReceivableResponseTapePreviewIdsQuery,
} from 'packages/loanProduct/requests/requests.gql.generated';
import { LoadingEmailServicerReportPreviewButton } from 'packages/loanProduct/ui/DownloadPreviewLoadingButtons';

export const EmailServicerReportPreviewButton = ({
  creditFacilityId,
  fileVersion,
  reportingDate,
  fileNamePrefix,
  previewIdentifier,
}: {
  creditFacilityId: string;
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  fileNamePrefix: string;
  previewIdentifier: string;
}) => {
  const toast = useToast();

  const {
    data: latestCompletedPreviewId,
    loading: latestCompletedPreviewIdIsLoading,
  } = useGetLatestCompletedReceivableResponseTapePreviewIdsQuery({
    variables: {
      creditFacilityIds: [creditFacilityId],
      reportingDate,
      fileVersion,
    },
  });

  const [
    createAssignmentServicerReportPreview,
  ] = useCreateAssignmentServicerReportPreviewMutation({
    onCompleted: () => {
      successToastConfig.title =
        'Creating servicing report preview from assignment proposals, please check your email in a few minutes.';
      toast(successToastConfig);
    },
    onError: () => {
      errorToastConfig.title = `Error creating servicing report preview from assignment proposals.`;
      toast(errorToastConfig);
    },
  });

  if (
    latestCompletedPreviewIdIsLoading ||
    !latestCompletedPreviewId ||
    !latestCompletedPreviewId.latestCompletedReceivableResponseTapePreviewIds
  ) {
    return <LoadingEmailServicerReportPreviewButton />;
  }

  const receivableResponseTapePreviewId =
    latestCompletedPreviewId.latestCompletedReceivableResponseTapePreviewIds[0];

  const createAndEmailServicerReportPreviewCallback = async () => {
    createAssignmentServicerReportPreview({
      variables: {
        assignmentServicerReportPreviewInput: {
          creditFacilityId,
          reportingDate,
          fileVersion,
          servicerReportPreviewType: 'assignmentServicerReportPreview',
          receivableResponseTapePreviewId,
          previewIdentifier,
        },
      },
    });
  };

  const fileName = `${fileNamePrefix}-${reportingDate}-v${fileVersion}`;

  return (
    <Button
      borderRadius="8px"
      colorScheme="aurora"
      variant="outline"
      border="2px solid"
      onClick={() => {
        createAndEmailServicerReportPreviewCallback();
        Sentry.addBreadcrumb({
          category: 'ui.click',
          message: `Clicked email servicer report preview from assignment ${fileName}`,
          level: Sentry.Severity.Info,
        });
      }}
    >
      Email Pro Forma Report
    </Button>
  );
};
